import dateFormat from 'dateformat';

import _ from '../locale';
import { DATA_TABLE_SECTION } from '../models/data-explorer-section';
import {
    AGE_GROUP_ID_KEY,
    GENDER_ID_KEY,
    LOCATION_ID_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
} from '../models/data-key';
import { ChartType, DataFilters, DataGranularity, DataGranularityKey, TimeUnitKey } from '../types';
import { localizeFilterValues } from './chart-data-helpers';
import { firstAndLastFromArray, formatValue } from './filters-helpers';

type ExportParams = {
    trigger: ChartType | typeof DATA_TABLE_SECTION | 'pdf_report';
    dataTool?: string;
    dataCollectionName?: string;
    conditions?: string | null;
    filters: DataFilters;
    selectedTimeUnit?: string | null;
    granularity?: DataGranularity | null;
    timeUnitKey?: TimeUnitKey | null;
};

/**
 * Returns filename in following format:
 * <Data Tool> - <Chart Type> Chart/Data/Report - <Conditions> -
 * <Measure> - <Unit> in <Selected Countries> - <Ages> -
 * <Genders> in <Time Unit(s)> - <Collection>
 */
const getFullExportName = (params: ExportParams, isShorten: boolean, skipParams = {}): string => {
    const {
        trigger,
        dataTool,
        dataCollectionName,
        conditions = null,
        filters,
        selectedTimeUnit = null,
        granularity = null,
        timeUnitKey = null,
    } = params;

    const getLocalizedGranularityValue = (granularityKey: DataGranularityKey): string | null => {
        if (filters && filters[granularityKey] && filters[granularityKey].length) {
            return localizeFilterValues(granularityKey, filters[granularityKey]);
        }
        return null;
    };

    const filenameParts: (string | null)[] = [];

    // Add data tool name
    const dataToolName = _(`data_tool_${dataTool}`);

    if (isShorten) {
        const date = new Date();
        return `${dataToolName} - ${dateFormat(date, 'yyyy-mm-dd - HH MM')}`;
    }

    !skipParams['dataToolName'] && filenameParts.push(dataToolName);

    // Add export trigger name
    const triggerName = _(`export_trigger_${trigger.replace('-', '_')}`);
    !skipParams['triggerName'] && filenameParts.push(triggerName);

    // Add conditions
    !skipParams['conditions'] && filenameParts.push(conditions);

    // Add measure
    !skipParams[MEASURE_ID_KEY] && filenameParts.push(getLocalizedGranularityValue(MEASURE_ID_KEY));

    // Add metric and location
    const metricInLocationParts: string[] = [];
    const metricName = getLocalizedGranularityValue(METRIC_ID_KEY);
    if (metricName) {
        metricInLocationParts.push(metricName);
    }
    const locationName = getLocalizedGranularityValue(LOCATION_ID_KEY);
    if (locationName) {
        metricInLocationParts.push(locationName);
    }
    filenameParts.push(metricInLocationParts.join(' in '));

    // Add age group
    !skipParams[AGE_GROUP_ID_KEY] &&
        filenameParts.push(getLocalizedGranularityValue(AGE_GROUP_ID_KEY));

    // Add gender and time
    const genderInTimeUnitParts: string[] = [];
    const genderName = getLocalizedGranularityValue(GENDER_ID_KEY);
    if (genderName) {
        genderInTimeUnitParts.push(genderName);
    }

    let timeUnit;
    if (selectedTimeUnit) {
        timeUnit = selectedTimeUnit;
    } else if (timeUnitKey && granularity && granularity[timeUnitKey]) {
        timeUnit = firstAndLastFromArray(granularity[timeUnitKey])
            ?.map((value) => formatValue(timeUnitKey, value, _))
            .join(' to ');
    }

    genderInTimeUnitParts.push(timeUnit);
    !skipParams[GENDER_ID_KEY] && filenameParts.push(genderInTimeUnitParts.join(' in '));

    // Add data collection
    !skipParams['dataCollectionName'] && filenameParts.push(dataCollectionName);

    return filenameParts.filter((v) => v != null).join(' - ');
};

/**
 * Returns filename in following format:
 * <Data Tool> - <Chart Type> Chart/Data/Report - <Conditions> -
 * <Measure> - <Unit> in <Selected Countries> - <Ages> -
 * <Genders> in <Time Unit(s)> - <Collection truncated to 108 characters
 * because of Excel and operating systems filename limitations with current
 * date and time in the yyyy-mm-dd HH-MM-ss format added to the end
 */
export const getExportFilename = (
    params: ExportParams,
    isShorten: boolean = false,
    skipParams = {}
): string => {
    const CHARS_LIMIT = 108;

    const formattedDate = dateFormat(new Date(), ' yyyy-mm-dd HH-MM-ss');
    const fullExportName = getFullExportName(params, isShorten, skipParams);

    if (fullExportName.length < CHARS_LIMIT) {
        return fullExportName + ' ' + formattedDate;
    }

    const truncatedExportName = fullExportName.substring(0, CHARS_LIMIT);
    const truncatedExportNameWords = truncatedExportName.split(' ');

    for (let i = truncatedExportNameWords.length - 1; i > 0; i -= 1) {
        if (truncatedExportNameWords[i].length > 2) {
            break;
        }

        delete truncatedExportNameWords[i];
    }

    return truncatedExportNameWords.join(' ').trim() + '... ' + formattedDate;
};
